import { supabase } from "@/integrations/supabase/client";
import { UserProfile, UserRole } from "@/types";
import { toast } from "sonner";
import { EmployeeStatus, ResidencyType, WorkerType } from '@/contexts/employees/EmployeesContext';
import { UserRole as UserRoleEnum } from '@/types/enums';

export const useRoleActions = (
  setUser: React.Dispatch<React.SetStateAction<UserProfile | null>>
) => {
  const switchRole = async (role: UserRole, user: UserProfile | null) => {
    if (user && (user.role === UserRoleEnum.APP_OWNER || user.role === UserRoleEnum.SUPER_ADMIN)) {
      try {
        setUser({
          ...user,
          role
        });
        
        toast.success(`Switched to ${role} role`);
      } catch {
        toast.error('Failed to switch role');
      }
    }
  };
  
  const impersonateUser = async (userId: string, user: UserProfile | null) => {
    if (!user) {
      toast.error('You need to be logged in to impersonate users');
      return;
    }
    
    if (user && (user.role === UserRoleEnum.APP_OWNER || user.role === UserRoleEnum.SUPER_ADMIN)) {
      try {
        // Check if super_admin is trying to impersonate a user outside their company
        if (user.role === UserRoleEnum.SUPER_ADMIN && user.companyId) {
          const { data: targetUser } = await supabase
            .from('profiles')
            .select('company_id')
            .eq('id', userId)
            .single();
            
          // If target user is from a different company, prevent impersonation
          if (targetUser && (targetUser as any).company_id !== user.companyId) {
            toast.error('You can only impersonate users within your company');
            return;
          }
        }
        
        // Only look in the profiles table for users to impersonate
        const { data: profileData, error } = await supabase
          .from('profiles')
          .select('*, companies:company_id(*)')
          .eq('id', userId)
          .single();
          
        if (error) {
          toast.error('Failed to fetch user data');
          return;
        }
          
        if (!profileData) {
          toast.error('User profile not found');
          return;
        }

        
        
        // Get the company data for the user to ensure analytics work properly
        let companyData = null;
        if ((profileData as any).company_id) {
          const { data: company } = await supabase
            .from('companies')
            .select('*')
            .eq('id', (profileData as any).company_id)
            .single();
            
          companyData = company;
        }
          
        const userData = {
          id: (profileData as any).id,
          email: (profileData as any).email,
          name: (profileData as any).name || (profileData as any).email,
          firstName: (profileData as any).first_name || '',
          lastName: (profileData as any).last_name || '',
          role: (profileData as any).role || UserRoleEnum.EMPLOYEE,
          department: (profileData as any).department || [],
          position: (profileData as any).position || '',
          shift: (profileData as any).shift || '',
          status: (profileData as any).status || EmployeeStatus.ACTIVE,
          residency: (profileData as any).residency || ResidencyType.RESIDENT,
          avatar: (profileData as any).avatar || '',
          isActive: (profileData as any).is_active !== false,
          createdAt: (profileData as any).created_at || new Date().toISOString(),
          updatedAt: (profileData as any).updated_at || new Date().toISOString(),
          workerType: (profileData as any).worker_type || WorkerType.FULL_TIME,
          companyId: (profileData as any).company_id || null,
          companyName: (profileData as any).companies?.name || (companyData?.name || null),
          // Add additional company data that might be needed for analytics
          companySettings: companyData ? {
            weekStartDay: companyData.week_start_day || 'monday',
            maxOvertimeHours: companyData.max_overtime_hours || 104
          } : null
        };
        
        setUser(userData);
        toast.success(`Now impersonating ${userData.name}`);

        // Force reload analytics data using requestIdleCallback for better performance
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
          window.requestIdleCallback(() => {
            // This will trigger a re-fetch of analytics data with the new user context
            window.location.reload();
          }, { timeout: 500 });
        } else {
          // Fallback for browsers without requestIdleCallback
          setTimeout(() => {
            // This will trigger a re-fetch of analytics data with the new user context
            window.location.reload();
          }, 500);
        }

      } catch {
        toast.error('Failed to impersonate user');
      }
    } else {
      toast.error('You don\'t have permission to impersonate users');
    }
  };

  return { switchRole, impersonateUser };
};
